/** Screen-space HUD: per-player coins / deaths, exit + controls hints, level messages. */
import type { Game } from '../sim/game.ts';
import type { PlayerController } from '../sim/player.ts';
import type { InputDevice } from './input.ts';

export interface HudState {
  game: Game | null;
  /** device bound to each player (null = waiting for a device) */
  bindings: (InputDevice | null)[];
  totalCoins: number;
  touch: boolean;
  muted: boolean;
  /** seconds since play started */
  time: number;
  message: string | null;
  error: string | null;
  maxPlayers: number;
  /** lobby: name under each player, and no "press a key to join" */
  names?: (string | null)[];
  hideJoin?: boolean;
  exitLabel?: string;
}

const css = (c: number[], a = 1) => `rgba(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)},${a})`;

export function drawHud(ctx: CanvasRenderingContext2D, w: number, h: number, s: HudState) {
  ctx.save();
  const u = Math.min(w, h);
  const fs = Math.round(Math.max(11, Math.min(16, u / 45)));
  ctx.font = `600 ${fs}px system-ui, -apple-system, Segoe UI, sans-serif`;
  ctx.textBaseline = 'middle';
  const pad = fs * 0.7;

  if (s.error) {
    panel(ctx, w / 2 - Math.min(w - 40, 560) / 2, h / 2 - fs * 3, Math.min(w - 40, 560), fs * 6);
    ctx.fillStyle = '#ff8080';
    ctx.textAlign = 'center';
    wrap(ctx, `Play mode failed: ${s.error}`, w / 2, h / 2 - fs, Math.min(w - 60, 540), fs * 1.3);
    ctx.fillStyle = '#ddd';
    ctx.fillText(s.touch ? 'Tap ✕ to exit' : 'Esc to exit', w / 2, h / 2 + fs * 2);
    ctx.restore();
    return;
  }
  const game = s.game;
  if (!game) {
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.textAlign = 'center';
    ctx.fillText('Loading…', w / 2, h / 2);
    ctx.restore();
    return;
  }

  // players
  const rows = game.players as PlayerController[];
  const lineH = fs * 1.6;
  const showJoin = !s.hideJoin && rows.length < s.maxPlayers;
  const panelW = Math.min(w - pad * 2, fs * 21);
  const panelH = pad + rows.length * lineH + (showJoin ? lineH * 0.9 : 0) + pad * 0.4;
  panel(ctx, pad, pad, panelW, panelH);
  ctx.textAlign = 'left';
  rows.forEach((p, i) => {
    const y = pad + pad * 0.6 + lineH * (i + 0.5);
    ctx.fillStyle = css(p.color);
    ctx.fillRect(pad * 2, y - fs * 0.45, fs * 0.9, fs * 0.9);
    ctx.fillStyle = '#fff';
    const coins = s.totalCoins ? `${p.coinCount}/${s.totalCoins}` : `${p.coinCount}`;
    ctx.fillText(`P${i + 1}`, pad * 2 + fs * 1.4, y);
    ctx.fillStyle = '#ffd94a';
    ctx.fillText(`● ${coins}`, pad * 2 + fs * 3.4, y);
    ctx.fillStyle = '#ff9a9a';
    ctx.fillText(p.livesLabel ? `♥ ${p.livesLabel}` : `✖ ${p.totalDeaths}`, pad * 2 + fs * 8, y);
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    const dev = s.bindings[i];
    ctx.font = `500 ${Math.round(fs * 0.8)}px system-ui, sans-serif`;
    const who = s.names?.[i] || (dev ? dev.label : s.hideJoin ? 'empty' : 'press any key / button');
    ctx.fillText(who, pad * 2 + fs * 11.2, y, panelW - fs * 12);
    ctx.font = `600 ${fs}px system-ui, -apple-system, Segoe UI, sans-serif`;
  });
  if (showJoin) {
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.font = `500 ${Math.round(fs * 0.8)}px system-ui, sans-serif`;
    const y = pad + pad * 0.6 + lineH * rows.length + lineH * 0.35;
    ctx.fillText(s.touch ? 'Join: press a button on a gamepad' : 'Join: arrow keys or a gamepad button', pad * 2, y, panelW - pad * 2);
  }

  // exit hint
  if (!s.touch) {
    ctx.font = `600 ${fs}px system-ui, sans-serif`;
    const t = s.exitLabel ?? `Esc to exit · P pause · M sound ${s.muted ? 'off' : 'on'}`;
    const tw = ctx.measureText(t).width;
    panel(ctx, w - tw - pad * 3, pad, tw + pad * 2, lineH);
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'left';
    ctx.fillText(t, w - tw - pad * 2, pad + lineH / 2);
  }

  // controls hint (fades after a while)
  const hintA = s.time < 12 ? 1 : Math.max(0.35, 1 - (s.time - 12) / 3);
  if (!s.touch) {
    ctx.globalAlpha = hintA;
    ctx.font = `500 ${Math.round(fs * 0.82)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    const lines = [
      'P1: WASD · Space jump · Shift/F dash · E use · Q powerup · R checkpoint · P pause · IJKL camera · Ctrl+IJKL fly · −/= hue · Z+Tab save · Z+− load',
      'P2: arrows to join · ↑ jump · . dash · , use   ·   Gamepad: A jump · X/Y dash · B use · Start pause · LB+right stick fly · d-pad colour · LT+up another player',
    ];
    const lh = fs * 1.25;
    const bw = Math.min(w - pad * 2, Math.max(...lines.map((l) => ctx.measureText(l).width)) + pad * 2);
    panel(ctx, w / 2 - bw / 2, h - pad - lh * lines.length - pad * 0.6, bw, lh * lines.length + pad * 0.6);
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    lines.forEach((l, i) => ctx.fillText(l, w / 2, h - pad - lh * (lines.length - i - 0.5) - pad * 0.3, bw - pad));
    ctx.globalAlpha = 1;
  }

  if (game.volleyHud) {
    ctx.font = `800 ${Math.round(fs * 1.4)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    const score = `${game.volleyHud.left}  –  ${game.volleyHud.right}`;
    const tw = ctx.measureText(score).width;
    panel(ctx, w / 2 - tw / 2 - pad, pad, tw + pad * 2, lineH);
    ctx.fillStyle = '#fff';
    ctx.fillText(score, w / 2, pad + lineH / 2);
  }

  // level messages
  if (s.message) {
    ctx.font = `800 ${Math.round(fs * 2.2)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    const tw = ctx.measureText(s.message).width;
    panel(ctx, w / 2 - tw / 2 - fs * 1.5, h * 0.3 - fs * 2, tw + fs * 3, fs * 4);
    ctx.fillStyle = '#fff';
    ctx.fillText(s.message, w / 2, h * 0.3);
  }
  ctx.restore();
}

export interface PauseHit {
  x: number;
  y: number;
  w: number;
  h: number;
  index: number;
}

/** Centered pause menu. Returns the button rectangles in the same CSS pixels as the pointer. */
export function drawPause(ctx: CanvasRenderingContext2D, w: number, h: number, title: string, labels: string[], selected: number): PauseHit[] {
  ctx.save();
  const u = Math.min(w, h);
  const fs = Math.round(Math.max(14, Math.min(22, u / 28)));
  const row = fs * 2.1;
  const bw = Math.min(w - 48, fs * 16);
  const bh = fs * 1.5 + row * labels.length + fs;
  const x = (w - bw) / 2;
  const y = (h - bh) / 2;
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fillRect(0, 0, w, h);
  panel(ctx, x, y, bw, bh);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `800 ${fs}px system-ui, sans-serif`;
  ctx.fillStyle = '#fff';
  ctx.fillText(title, w / 2, y + fs);
  const hits: PauseHit[] = [];
  labels.forEach((label, i) => {
    const by = y + fs * 1.6 + row * i;
    const bx = x + fs * 0.6;
    const buttonW = bw - fs * 1.2;
    const buttonH = row * 0.86;
    ctx.fillStyle = i === selected ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.06)';
    ctx.beginPath();
    ctx.roundRect(bx, by, buttonW, buttonH, 6);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = `650 ${Math.round(fs * 0.85)}px system-ui, sans-serif`;
    ctx.fillText(label, w / 2, by + buttonH / 2);
    hits.push({ x: bx, y: by, w: buttonW, h: buttonH, index: i });
  });
  ctx.restore();
  return hits;
}

function panel(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  const r = Math.min(8, h / 2);
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.fill();
}

function wrap(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, lh: number) {
  const words = text.split(/\s+/);
  let line = '';
  let yy = y;
  for (const wd of words) {
    const t = line ? `${line} ${wd}` : wd;
    if (ctx.measureText(t).width > maxW && line) {
      ctx.fillText(line, x, yy);
      line = wd;
      yy += lh;
    } else line = t;
  }
  if (line) ctx.fillText(line, x, yy);
}
